import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type EmailSettings = {
  email: string;
  sender_name: string;
  protocol: string;
  host: string;
  port: number;
  username: string;
  security_type: string;
  has_password: boolean;
  has_password_ciphertext: boolean;
  password_secret_invalid: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_security_type: string;
  smtp_from_email: string;
  has_smtp_password: boolean;
  has_smtp_password_ciphertext: boolean;
  smtp_password_secret_invalid: boolean;
  outbound_ready: boolean;
  sos_email_dev_bypass: boolean;
  updated_at: string | null;
};

const EMPTY: EmailSettings = {
  email: '',
  sender_name: '',
  protocol: 'imap',
  host: '',
  port: 993,
  username: '',
  security_type: 'ssl',
  has_password: false,
  has_password_ciphertext: false,
  password_secret_invalid: false,
  smtp_host: 'smtp.sendgrid.net',
  smtp_port: 587,
  smtp_username: 'apikey',
  smtp_security_type: 'tls',
  smtp_from_email: '',
  has_smtp_password: false,
  has_smtp_password_ciphertext: false,
  smtp_password_secret_invalid: false,
  outbound_ready: false,
  sos_email_dev_bypass: false,
  updated_at: null,
};

export function MasterEmailPage() {
  const [status, setStatus] = useState<EmailSettings>(EMPTY);
  const [form, setForm] = useState({
    email: '',
    sender_name: '',
    protocol: 'imap',
    host: '',
    port: '993',
    username: '',
    password: '',
    security_type: 'ssl',
    smtp_host: 'smtp.sendgrid.net',
    smtp_port: '587',
    smtp_username: 'apikey',
    smtp_password: '',
    smtp_security_type: 'tls',
    smtp_from_email: '',
  });
  const [passwordEdited, setPasswordEdited] = useState(false);
  const [smtpPasswordEdited, setSmtpPasswordEdited] = useState(false);
  const [forceRekey, setForceRekey] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    const res = await apiFetch('/api/v1/master/system/email');
    const body = (await res.json().catch(() => ({}))) as EmailSettings & {
      error?: string;
      message?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
    setStatus({ ...EMPTY, ...body });
    setForm({
      email: String(body.email || ''),
      sender_name: String(body.sender_name || ''),
      protocol: String(body.protocol || 'imap'),
      host: String(body.host || ''),
      port: body.port != null ? String(body.port) : '993',
      username: String(body.username || ''),
      password: '',
      security_type: String(body.security_type || 'ssl'),
      smtp_host: String(body.smtp_host || 'smtp.sendgrid.net'),
      smtp_port: body.smtp_port != null ? String(body.smtp_port) : '587',
      smtp_username: String(body.smtp_username || 'apikey'),
      smtp_password: '',
      smtp_security_type: String(body.smtp_security_type || 'tls'),
      smtp_from_email: String(body.smtp_from_email || ''),
    });
    setPasswordEdited(false);
    setSmtpPasswordEdited(false);
    setForceRekey(false);
    if (!testTo && body.email) setTestTo(String(body.email));
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patchField(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMessage(null);
    setError(null);
  }

  async function onSave() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        email: form.email.trim(),
        sender_name: form.sender_name.trim(),
        protocol: form.protocol,
        host: form.host.trim(),
        port: Number(form.port) || 993,
        username: form.username.trim(),
        security_type: form.security_type,
        smtp_host: form.smtp_host.trim(),
        smtp_port: Number(form.smtp_port) || 587,
        smtp_username: form.smtp_username.trim(),
        smtp_security_type: form.smtp_security_type,
        smtp_from_email: form.smtp_from_email.trim(),
        force_rekey: forceRekey,
      };
      if (passwordEdited && form.password.trim()) {
        payload.password = form.password.trim();
      }
      if (smtpPasswordEdited && form.smtp_password.trim()) {
        payload.smtp_password = form.smtp_password.trim();
      }
      const res = await apiFetch('/api/v1/master/system/email', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      await load();
      setMessage('Impostazioni email salvate (password/API key mai restituite in chiaro).');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onTest() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        to: testTo.trim(),
        email: form.email.trim(),
        sender_name: form.sender_name.trim(),
        smtp_from_email: form.smtp_from_email.trim() || form.email.trim(),
      };
      if (smtpPasswordEdited && form.smtp_password.trim()) {
        payload.smtp_password = form.smtp_password.trim();
      }
      const res = await apiFetch('/api/v1/master/system/email/test', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
        ok?: boolean;
      };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(body.message || 'Test inviato');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const needsRekey =
    status.password_secret_invalid || status.smtp_password_secret_invalid;
  const outboundTone = status.outbound_ready ? 'ok' : 'warn';

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Email Master"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · SMTP SendGrid per SOS e mail transazionali. Secret
            cifrati in DB; wrap key = MASTER_EMAIL_SETTINGS_SECRET.
          </>
        }
        actions={
          <>
            <button type="button" className="sk-btn sk-btn--quiet" disabled={busy} onClick={() => void onTest()}>
              Test invio
            </button>
            <button type="button" className="sk-btn" disabled={busy} onClick={() => void onSave()}>
              {busy ? 'Salvataggio…' : 'Salva'}
            </button>
          </>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <div className="sk-theme-layout">
        <SectionCard title="Outbound">
          <p className="sk-box__label">Stato</p>
          <p className="sk-box__body">
            Pronto:{' '}
            <strong className={`sk-tone sk-tone--${outboundTone}`}>
              {status.outbound_ready ? 'Sì' : 'No'}
            </strong>
            {status.sos_email_dev_bypass ? ' · SOS_EMAIL_DEV_BYPASS=1' : null}
          </p>
          {status.updated_at ? (
            <p className="sk-muted">Aggiornato: {new Date(status.updated_at).toLocaleString()}</p>
          ) : null}
          {needsRekey ? (
            <p className="sk-error" style={{ marginTop: '0.75rem' }}>
              Ciphertext non decifrabile. Per riscrivere: spunta force_rekey e
              ALLOW_EMAIL_SETTINGS_REKEY=1 sul runtime.
            </p>
          ) : null}
        </SectionCard>

        <SectionCard title="Identità">
          <p className="sk-box__label">Mittente</p>
          <div className="sk-theme-fields">
            <label className="sk-field">
              <span>Email account</span>
              <input
                className="sk-input"
                value={form.email}
                onChange={(e) => patchField('email', e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="sk-field">
              <span>Nome mittente</span>
              <input
                className="sk-input"
                value={form.sender_name}
                onChange={(e) => patchField('sender_name', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>From SMTP</span>
              <input
                className="sk-input"
                value={form.smtp_from_email}
                onChange={(e) => patchField('smtp_from_email', e.target.value)}
                placeholder="noreply@…"
              />
            </label>
          </div>
        </SectionCard>

        <SectionCard title="SendGrid">
          <p className="sk-box__label">SMTP</p>
          <div className="sk-theme-fields">
            <label className="sk-field">
              <span>Host</span>
              <input
                className="sk-input"
                value={form.smtp_host}
                onChange={(e) => patchField('smtp_host', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Porta</span>
              <input
                className="sk-input"
                value={form.smtp_port}
                onChange={(e) => patchField('smtp_port', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Username</span>
              <input
                className="sk-input"
                value={form.smtp_username}
                onChange={(e) => patchField('smtp_username', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>API Key (smtp_password)</span>
              <input
                className="sk-input"
                type="password"
                autoComplete="new-password"
                value={form.smtp_password}
                placeholder={
                  status.has_smtp_password && !smtpPasswordEdited
                    ? '•••••••• (già salvata — lascia vuoto)'
                    : 'Incolla API key SendGrid'
                }
                onChange={(e) => {
                  setSmtpPasswordEdited(true);
                  patchField('smtp_password', e.target.value);
                }}
              />
            </label>
          </div>
        </SectionCard>

        <SectionCard title="IMAP / POP (opzionale)">
          <p className="sk-box__label">Casella</p>
          <div className="sk-theme-fields">
            <label className="sk-field">
              <span>Host</span>
              <input
                className="sk-input"
                value={form.host}
                onChange={(e) => patchField('host', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Porta</span>
              <input
                className="sk-input"
                value={form.port}
                onChange={(e) => patchField('port', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Username</span>
              <input
                className="sk-input"
                value={form.username}
                onChange={(e) => patchField('username', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Password casella</span>
              <input
                className="sk-input"
                type="password"
                autoComplete="new-password"
                value={form.password}
                placeholder={
                  status.has_password && !passwordEdited
                    ? '•••••••• (già salvata — lascia vuoto)'
                    : 'Password IMAP/POP'
                }
                onChange={(e) => {
                  setPasswordEdited(true);
                  patchField('password', e.target.value);
                }}
              />
            </label>
          </div>
        </SectionCard>

        <SectionCard title="Invio prova">
          <p className="sk-box__label">Test / break-glass</p>
          <div className="sk-theme-fields">
            <label className="sk-field">
              <span>Destinatario test</span>
              <input
                className="sk-input"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="tu@esempio.com"
              />
            </label>
            <label className="sk-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={forceRekey}
                onChange={(e) => setForceRekey(e.target.checked)}
              />
              <span>force_rekey (richiede anche ALLOW_EMAIL_SETTINGS_REKEY=1)</span>
            </label>
          </div>
        </SectionCard>
      </div>
    </section>
  );
}
